import Modal from "components/DesignSystem/Modal/Modal";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import { Field, FieldProps, Form, Formik } from "formik";
import RadioGroup from "components/DesignSystem/RadioGroup/RadioGroup";
import {
  addRegistrationAdminSchema,
  addRegistrationSchema,
} from "formValidations/addRegistrationSchema";
import { Button } from "components/DesignSystem/Button/Button";
import { ToolTipIcon } from "components/icons/TooltipIcon";
import { DateInput } from "components/DesignSystem/DateInput/DateInput";
import ToolTip from "components/design/toolTip";
import {
  useCreatePreSalesRegistraionsMutation,
  useUpdatePreSalesTaxRegistrationsMutation,
} from "store/apis/salesTaxRegistration";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useToast } from "hooks/useToast";
import { BackendError } from "types/utils/error";
import {
  getSelectedState,
  initialRegistrationDataType,
} from "./SalesTaxRegistrations";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { DISPLAY_DATE } from "constants/date";

export const AddRegistrationModal = ({
  open,
  onClose,
  stateOptions,
  initialRegistrationData,
}: {
  open: boolean;
  onClose: () => void;
  stateOptions?: { label: string; value: string }[];
  initialRegistrationData?: initialRegistrationDataType;
}) => {
  const [updateSalesTaxRegistration, { isLoading: isUpdateSalesTaxLoading }] =
    useUpdatePreSalesTaxRegistrationsMutation();
  const [createSalesTaxRegistration, { isLoading: isCreateSalexTaxLoading }] =
    useCreatePreSalesRegistraionsMutation();
  const entityId = useCurrentEntityId();
  const { successToast, alertToast } = useToast();

  const handleCreateSalesTaxRegistration = async (values: any) => {
    const { existingTaxAccount, state_registration_id, ...restValues } = values;

    try {
      await createSalesTaxRegistration({
        entityId,
        payload: restValues,
      }).unwrap();
      successToast({
        message: `Details added for ${getSelectedState(
          values.state_id,
          stateOptions
        )}`,
      });
      onClose();
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  const handleUpdateSalesTaxRegistration = async (values: any) => {
    try {
      await updateSalesTaxRegistration({
        entityId,
        state_registration_id:
          initialRegistrationData?.state_registration_id as string,
        payload: values,
      });
      onClose();
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  const handleSubmit = async (values: any) => {
    if (initialRegistrationData?.state_registration_id?.length) {
      handleUpdateSalesTaxRegistration(values);
    } else {
      handleCreateSalesTaxRegistration(values);
    }
  };

  const { isAdmin } = useRoleBasedView();

  return (
    <Modal.Root open={open} onOpenChange={onClose}>
      <Modal.Content>
        <Formik
          validationSchema={
            isAdmin ? addRegistrationAdminSchema : addRegistrationSchema
          }
          initialValues={{
            tax_detail_id: "",
            state_id: initialRegistrationData?.state_id || "",
            existingTaxAccount: "YES",
            state_registration_id:
              initialRegistrationData?.state_registration_id || "",
          }}
          onSubmit={handleSubmit}
        >
          {({ setFieldValue, values }) => {
            return (
              <Form>
                <Modal.Header>
                  <Modal.Title>
                    Add Registrations{" "}
                    {initialRegistrationData?.state &&
                      `(${initialRegistrationData?.state})`}
                  </Modal.Title>
                  <Modal.Close />
                </Modal.Header>
                <Modal.Body>
                  <div className="t-flex t-gap-y-3 t-flex-col">
                    {!initialRegistrationData?.state_id && (
                      <Combobox
                        withForm
                        size="regular"
                        label="State"
                        name={`state_id`}
                        defaultValue={
                          initialRegistrationData?.state_registration_id
                            ? {
                                value:
                                  initialRegistrationData?.state_registration_id,
                                label: initialRegistrationData?.state,
                              }
                            : null
                        }
                        required
                        options={stateOptions}
                        isClearable={false}
                      />
                    )}
                    <Field name="filing_start_date">
                      {({ field }: FieldProps) => {
                        return (
                          <DateInput
                            {...field}
                            required={!isAdmin}
                            label={
                              <div className="t-inline-flex t-gap-x-1 t-items-center">
                                <span>Filing service start date</span>
                                <ToolTip
                                  side="right"
                                  text={
                                    <span>
                                      Pick the first day of the tax period you
                                      want us <br />
                                      to handle (e.g., April 1 for Q2 filing)
                                    </span>
                                  }
                                >
                                  <div>
                                    <ToolTipIcon />
                                  </div>
                                </ToolTip>
                              </div>
                            }
                            placeholder={DISPLAY_DATE}
                            portalId="validOnDate"
                          />
                        );
                      }}
                    </Field>
                    <div>
                      <p className="t-text-text-30 t-text-body-sm t-mb-3">
                        Do you have existing tax account ID in this state?
                      </p>
                      <RadioGroup.Root
                        required
                        onValueChange={(value) => {
                          setFieldValue("existingTaxAccount", value);
                        }}
                        defaultValue={values.existingTaxAccount}
                        className="t-flex t-flex-row t-gap-3 t-text-body"
                      >
                        <RadioGroup.Item value="YES" className="t-max-w-14">
                          Yes
                        </RadioGroup.Item>
                        <RadioGroup.Item value="NO">No</RadioGroup.Item>
                      </RadioGroup.Root>
                    </div>
                    {values.existingTaxAccount === "YES" && (
                      <TextInput
                        label="Existing tax accounts ID in this state"
                        placeholder="0123456789, 0213865721"
                        note={
                          <span className="t-text-neutral-40 t-text-body-sm">
                            e.g. Franchise Tax ID, Withholding Tax ID, SOS ID,
                            Payroll Tax ID, etc.
                          </span>
                        }
                        name="tax_detail_id"
                      />
                    )}
                  </div>
                </Modal.Body>
                <Modal.FooterButtonGroup>
                  <Button onClick={onClose}>Cancel</Button>
                  <Button
                    type="submit"
                    disabled={
                      isCreateSalexTaxLoading || isUpdateSalesTaxLoading
                    }
                    isLoading={
                      isCreateSalexTaxLoading || isUpdateSalesTaxLoading
                    }
                    customType="primary"
                  >
                    Add
                  </Button>
                </Modal.FooterButtonGroup>
              </Form>
            );
          }}
        </Formik>
      </Modal.Content>
    </Modal.Root>
  );
};
